export class MemoryStorageAdapter {
    values = new Map();
    async get(key) {
        return (this.values.has(key) ? this.values.get(key) : null);
    }
    async set(key, value) {
        this.values.set(key, value);
    }
    async remove(key) {
        this.values.delete(key);
    }
}
export class IndexedDbKeyValueStorage {
    dbName;
    storeName;
    version;
    dbPromise = null;
    constructor(dbName = 'SkyChatMobileDB', storeName = 'keyValue', version = 1) {
        this.dbName = dbName;
        this.storeName = storeName;
        this.version = version;
    }
    open() {
        if (this.dbPromise)
            return this.dbPromise;
        this.dbPromise = new Promise((resolve, reject) => {
            const req = indexedDB.open(this.dbName, this.version);
            req.onerror = () => reject(req.error);
            req.onsuccess = () => resolve(req.result);
            req.onupgradeneeded = () => {
                if (!req.result.objectStoreNames.contains(this.storeName)) {
                    req.result.createObjectStore(this.storeName);
                }
            };
        });
        return this.dbPromise;
    }
    async get(key) {
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readonly');
            const req = tx.objectStore(this.storeName).get(key);
            req.onsuccess = () => resolve((req.result ?? null));
            req.onerror = () => reject(req.error);
        });
    }
    async set(key, value) {
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            tx.objectStore(this.storeName).put(value, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    async remove(key) {
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            tx.objectStore(this.storeName).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
}
export class NativeSecureStorageAdapter {
    pluginPromise = null;
    async plugin() {
        if (this.pluginPromise)
            return this.pluginPromise;
        this.pluginPromise = import('@aparajita/capacitor-secure-storage');
        return this.pluginPromise;
    }
    async get(key) {
        const plugin = await this.plugin();
        const api = (plugin.SecureStorage || plugin.default || plugin.CapacitorSecureStorage);
        if (!api)
            return null;
        const result = api.getItem
            ? await api.getItem(key)
            : api.get
                ? await api.get(key, false)
                : null;
        const value = typeof result === 'string' ? result : result?.value ?? result?.data ?? result ?? null;
        if (value == null)
            return null;
        return JSON.parse(String(value));
    }
    async set(key, value) {
        const plugin = await this.plugin();
        const api = (plugin.SecureStorage || plugin.default || plugin.CapacitorSecureStorage);
        if (!api)
            return;
        const serialized = JSON.stringify(value);
        if (api.setItem)
            await api.setItem(key, serialized);
        else if (api.set)
            await api.set(key, value);
    }
    async remove(key) {
        const plugin = await this.plugin();
        const api = (plugin.SecureStorage || plugin.default || plugin.CapacitorSecureStorage);
        if (api?.removeItem)
            await api.removeItem(key);
        else if (api?.remove)
            await api.remove(key);
        else if (api?.delete)
            await api.delete(key);
    }
}
export async function createSecretStorage() {
    try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.getPlatform() !== 'web') {
            return new NativeSecureStorageAdapter();
        }
    }
    catch {
        // Browser-only tests and PWA builds fall through to IndexedDB.
    }
    if (typeof indexedDB !== 'undefined') {
        return new IndexedDbKeyValueStorage('SkyChatMobileSecrets', 'secrets');
    }
    return new MemoryStorageAdapter();
}
export async function migrateLegacyKeypair(storage, legacy = globalThis.localStorage ?? null) {
    const existing = await storage.get('keypair');
    if (existing?.myPrivJwk && existing?.myPubJwk)
        return existing;
    if (!legacy)
        return null;
    const pubRaw = legacy.getItem('skychat_pub');
    const privRaw = legacy.getItem('skychat_priv');
    if (!pubRaw || !privRaw)
        return null;
    const keypair = {
        myPubJwk: JSON.parse(pubRaw),
        myPrivJwk: JSON.parse(privRaw)
    };
    await storage.set('keypair', keypair);
    return keypair;
}
//# sourceMappingURL=storage.js.map